/* eslint-disable react/prop-types */
import { Card, Typography } from "antd";
import {
  COUNTY_RAMP_STOPS,
  SECTION_RAMP_STOPS,
} from "../utils/mapRamps";

// Floating legend for the deals map. Renders a different color strip
// depending on whether the user is currently focused on counties (default)
// or sections (zoom >= 11). The parent owns the zoom-watching logic and
// passes `mode` plus the matching `maxCount`.
//
// Props:
//   maxCount: number — current max value for the active layer's choropleth
//   theme: "light" | "dark"
//   mode: "counties" | "sections"
//   mode "tracts": no choropleth ramp (tract shapes / section dots carry it)
//   tractBuckets: Array<{ key, label, color }> — tract status buckets drawn
//   activeDefinition: string — the page's one definition of "active"
//   occTitle: heading for the OCC section
//   occKinds: Array<{ kind, label, color, shape? }> — enabled OCC overlays
//     only; shape "dot" for a point overlay (default: outline swatch);
//     empty/omitted renders no OCC section

const SWATCH_SIZE = 16;

const Swatch = ({ color, label, transparent, theme }) => {
  const borderColor = theme === "dark" ? "#555" : "#bdbdbd";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        minWidth: 28,
      }}
    >
      <div
        style={{
          width: SWATCH_SIZE,
          height: SWATCH_SIZE,
          background: transparent ? "transparent" : color,
          border: `1px solid ${borderColor}`,
          boxSizing: "border-box",
        }}
      />
      <Typography.Text
        style={{
          fontSize: 10,
          lineHeight: "12px",
          color: theme === "dark" ? "#ddd" : "#595959",
        }}
      >
        {label}
      </Typography.Text>
    </div>
  );
};

// Build the swatch list for a given ramp + maxCount. We always include the
// transparent "0" swatch first, then the ramp stops in increasing order.
const buildSwatches = (rampStops, maxCount) => {
  const safeMax = Math.max(1, maxCount || 1);
  const result = [{ color: "transparent", label: "0", transparent: true }];
  // The first county stop has fraction:null and represents the count=1 floor
  // bucket; we treat it specially so the label reads "1".
  for (let i = 0; i < rampStops.length; i++) {
    const stop = rampStops[i];
    if (stop.fraction === null) {
      result.push({ color: stop.color, label: "1" });
      continue;
    }
    if (stop.fraction === 0) {
      // Section ramp's lowest stop covers count==0; we already represented
      // that with the transparent box, so skip pure-zero ramp entries to
      // avoid duplicates.
      continue;
    }
    const value = Math.round(safeMax * stop.fraction);
    // Force minimum of 1 so labels never read "0" past the transparent box
    // when safeMax is small.
    const display = Math.max(1, value);
    result.push({ color: stop.color, label: String(display) });
  }
  // Dedup adjacent labels (small maxCount can collapse e.g. 25%/56% to same
  // rounded value) — keep first occurrence so colors progress.
  const seen = new Set();
  return result.filter((s) => {
    if (seen.has(s.label)) return false;
    seen.add(s.label);
    return true;
  });
};

// Outline swatch for an OCC unit kind — the map draws units as outlines.
const LineSwatch = ({ color }) => (
  <div
    style={{
      width: SWATCH_SIZE,
      height: SWATCH_SIZE - 4,
      border: `2px solid ${color}`,
      boxSizing: "border-box",
      flex: "none",
    }}
  />
);

// Dot swatch for a point overlay (OCC intents to drill) — drawn as circles.
const DotSwatch = ({ color }) => (
  <div style={{ width: SWATCH_SIZE, display: "flex", justifyContent: "center", flex: "none" }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: color,
        border: "1.5px solid #fff",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.25)",
        boxSizing: "border-box",
      }}
    />
  </div>
);

const DealsMapLegend = ({
  maxCount,
  theme,
  mode,
  occKinds = [],
  activeDefinition,
  tractBuckets = [],
  occTitle = "OCC units",
}) => {
  // The count ramps belong to the choropleth modes; "tracts" (the deals
  // map today) shows only the status buckets.
  const showRamp = mode === "counties" || mode === "sections";
  const isDark = theme === "dark";
  const isSections = mode === "sections";

  const title = isSections ? "Active deals by section" : "Active deals by county";
  const rampStops = isSections ? SECTION_RAMP_STOPS : COUNTY_RAMP_STOPS;
  const swatches = buildSwatches(rampStops, maxCount);

  const cardStyle = {
    position: "absolute",
    bottom: 24,
    left: 24,
    width: "auto",
    maxWidth: 280,
    background: isDark ? "rgba(31,31,31,0.95)" : "rgba(255,255,255,0.96)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    border: isDark ? "1px solid #333" : "1px solid #e8e8e8",
    pointerEvents: "auto",
    zIndex: 5,
  };

  const bodyTextColor = isDark ? "#eee" : undefined;
  const mutedColor = isDark ? "#aaa" : "#8c8c8c";

  return (
    <Card
      size="small"
      style={cardStyle}
      styles={{ body: { padding: 10 } }}
    >
      {showRamp ? (
        <Typography.Text
          strong
          style={{
            fontSize: 12,
            display: "block",
            marginBottom: 6,
            color: bodyTextColor,
          }}
        >
          {title}
        </Typography.Text>
      ) : null}

      {!showRamp ? null : (maxCount || 0) === 0 ? (
        <Typography.Text
          style={{ fontSize: 11, color: mutedColor, fontStyle: "italic" }}
        >
          No deals in current view
        </Typography.Text>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 6,
          }}
        >
          {swatches.map((s, i) => (
            <Swatch
              key={`${s.label}-${i}`}
              color={s.color}
              label={s.label}
              transparent={s.transparent}
              theme={theme}
            />
          ))}
        </div>
      )}

      {isSections ? (
        <Typography.Text
          style={{
            display: "block",
            marginTop: 8,
            fontSize: 10,
            color: mutedColor,
          }}
        >
          Number on each section is its SEC (1–36)
        </Typography.Text>
      ) : null}

      {tractBuckets.length > 0 ? (
        <div style={{ marginTop: showRamp ? 8 : 0 }}>
          <Typography.Text
            strong
            style={{ fontSize: 12, display: "block", color: bodyTextColor }}
          >
            Tracts by deal status
          </Typography.Text>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {tractBuckets.map((b) => (
              <Swatch key={b.key} color={b.color} label={b.label} theme={theme} />
            ))}
          </div>
        </div>
      ) : null}

      {occKinds.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <Typography.Text
            strong
            style={{ fontSize: 12, display: "block", color: bodyTextColor }}
          >
            {occTitle}
          </Typography.Text>
          {occKinds.map((k) => (
            <div
              key={k.kind}
              style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}
            >
              {k.shape === "dot" ? <DotSwatch color={k.color} /> : <LineSwatch color={k.color} />}
              <Typography.Text style={{ fontSize: 11, color: bodyTextColor }}>
                {k.label}
              </Typography.Text>
            </div>
          ))}
        </div>
      ) : null}

      {activeDefinition ? (
        <Typography.Text
          style={{
            display: "block",
            marginTop: isSections ? 4 : 8,
            fontSize: 10,
            color: mutedColor,
          }}
        >
          {activeDefinition}
        </Typography.Text>
      ) : null}
    </Card>
  );
};

export default DealsMapLegend;
